// ============================================================
// THE COMPANY — SidePanel Component
// ============================================================

import { useState, useRef, useEffect } from "react";
import { useCompanyStore } from "../../store/useCompanyStore";
import RoleProfile from "./RoleProfile";
import StatusSelect from "./StatusSelect";
import RoleNotes from "./RoleNotes";
import KnowledgeMap from "./KnowledgeMap";
import ConversationLog from "./ConversationLog";
import ConversationForm from "./ConversationForm";
import ActionItems from "./ActionItems";
import PromptBuilder from "../prompt/PromptBuilder";
import BriefingBuilder from "../prompt/BriefingBuilder";
import Modal from "../shared/Modal";
import { generateRolePrompt } from "../../lib/promptTemplates";

export default function SidePanel() {
  const selectedRoleId = useCompanyStore((s) => s.selectedRoleId);
  const roles = useCompanyStore((s) => s.roles);
  const decisions = useCompanyStore((s) => s.decisions);
  const selectRole = useCompanyStore((s) => s.selectRole);
  const deleteRole = useCompanyStore((s) => s.deleteRole);
  const [promptOpen, setPromptOpen] = useState(false);
  const [briefingOpen, setBriefingOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  const role = roles.find((r) => r.id === selectedRoleId) ?? null;

  // The parent Dashboard container handles width (420px), border, and
  // conditional rendering. This component simply fills its container.
  return (
    <aside
      className="h-full flex flex-col"
      style={{ backgroundColor: "var(--bg-surface)" }}
    >
      {role && (
        <>
          {/* Header */}
          <div
            className="flex items-center justify-between px-5 py-4 shrink-0"
            style={{
              borderBottom: "1px solid var(--border-subtle)",
              backgroundImage:
                "linear-gradient(to bottom, rgba(99, 102, 241, 0.03), transparent)",
            }}
          >
            <h2
              className="text-base font-semibold truncate"
              style={{ color: "var(--text-primary)" }}
            >
              {role.name}
            </h2>
            <div className="flex items-center gap-1.5 shrink-0">
              {/* Copy Prompt */}
              <button
                type="button"
                onClick={async () => {
                  const informedDecisions = decisions.filter((d) =>
                    d.informedRoleIds.includes(role.id),
                  );
                  const text = generateRolePrompt(
                    role,
                    roles,
                    informedDecisions,
                  );
                  await navigator.clipboard.writeText(text);
                  setCopied(true);
                  if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
                  copyTimerRef.current = setTimeout(
                    () => setCopied(false),
                    2000,
                  );
                }}
                className="flex items-center justify-center h-7 rounded-md cursor-pointer transition-colors duration-150 text-[10px] font-medium px-2 gap-1"
                style={{
                  color: copied ? "#22c55e" : "var(--text-muted)",
                  backgroundColor: "transparent",
                  border: "none",
                }}
                onMouseEnter={(e) => {
                  if (!copied) {
                    e.currentTarget.style.backgroundColor =
                      "var(--bg-elevated)";
                    e.currentTarget.style.color = "var(--text-primary)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!copied) {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.color = "var(--text-muted)";
                  }
                }}
                title="Copy role prompt to clipboard"
              >
                {copied ? (
                  <>
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Copied
                  </>
                ) : (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                onClick={() => selectRole(null)}
                className="flex items-center justify-center h-7 w-7 rounded-md cursor-pointer transition-colors duration-150"
                style={{
                  color: "var(--text-muted)",
                  backgroundColor: "transparent",
                  border: "none",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor =
                    "var(--bg-elevated)";
                  (e.currentTarget as HTMLElement).style.color =
                    "var(--text-primary)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor =
                    "transparent";
                  (e.currentTarget as HTMLElement).style.color =
                    "var(--text-muted)";
                }}
                title="Close panel"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <line x1="2" y1="2" x2="12" y2="12" />
                  <line x1="12" y1="2" x2="2" y2="12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
            <RoleProfile key={role.id} role={role} />

            <Divider />
            <StatusSelect role={role} />

            <Divider />
            <RoleNotes key={`notes-${role.id}`} role={role} />

            <Divider />
            <ActionItems roleId={role.id} />

            <Divider />
            <KnowledgeMap roleId={role.id} />

            <Divider />
            <ConversationLog roleId={role.id} />

            <Divider />
            <ConversationForm roleId={role.id} />

            <Divider />
            {/* Action buttons */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPromptOpen(true)}
                className="flex-1 rounded-md border px-3 py-2 text-xs font-medium transition-colors duration-150"
                style={{
                  borderColor: "var(--border-default)",
                  color: "var(--text-secondary)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--accent)";
                  e.currentTarget.style.color = "var(--accent)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--border-default)";
                  e.currentTarget.style.color = "var(--text-secondary)";
                }}
              >
                Generate Prompt
              </button>
              <button
                type="button"
                onClick={() => setBriefingOpen(true)}
                className="flex-1 rounded-md border px-3 py-2 text-xs font-medium transition-colors duration-150"
                style={{
                  borderColor: "var(--border-default)",
                  color: "var(--text-secondary)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--accent)";
                  e.currentTarget.style.color = "var(--accent)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--border-default)";
                  e.currentTarget.style.color = "var(--text-secondary)";
                }}
              >
                Build Briefing
              </button>
            </div>

            <Divider />
            {/* Danger zone */}
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full rounded-md border px-3 py-2 text-xs font-medium transition-colors duration-150"
              style={{
                borderColor: "rgba(239, 68, 68, 0.3)",
                color: "#ef4444",
                backgroundColor: "transparent",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  "rgba(239, 68, 68, 0.1)";
                e.currentTarget.style.borderColor = "#ef4444";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.borderColor = "rgba(239, 68, 68, 0.3)";
              }}
            >
              Delete Role
            </button>
          </div>

          {/* Modals */}
          <PromptBuilder
            roleId={role.id}
            isOpen={promptOpen}
            onClose={() => setPromptOpen(false)}
          />
          <BriefingBuilder
            roleId={role.id}
            isOpen={briefingOpen}
            onClose={() => setBriefingOpen(false)}
          />
          <Modal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            title="Delete Role"
            width="400px"
          >
            <p
              className="text-sm mb-4"
              style={{ color: "var(--text-secondary)" }}
            >
              Delete{" "}
              <strong style={{ color: "var(--text-primary)" }}>
                {role.name}
              </strong>
              ? This will remove the role and all their conversations. This
              cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-md border px-3 py-1.5 text-xs font-medium transition-colors duration-150"
                style={{
                  borderColor: "var(--border-default)",
                  color: "var(--text-secondary)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--border-strong)";
                  e.currentTarget.style.color = "var(--text-primary)";
                  e.currentTarget.style.backgroundColor = "var(--bg-elevated)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--border-default)";
                  e.currentTarget.style.color = "var(--text-secondary)";
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={async () => {
                  if (deleting) return;
                  setDeleting(true);
                  try {
                    await deleteRole(role.id);
                    setShowDeleteConfirm(false);
                  } finally {
                    setDeleting(false);
                  }
                }}
                className="rounded-md px-3 py-1.5 text-xs font-medium transition-colors duration-150"
                style={{
                  backgroundColor: "#ef4444",
                  color: "#ffffff",
                  border: "none",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#dc2626";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#ef4444";
                }}
              >
                Delete
              </button>
            </div>
          </Modal>
        </>
      )}
    </aside>
  );
}

/** Thin horizontal divider between sections */
function Divider() {
  return (
    <hr
      className="border-0 h-px"
      style={{ backgroundColor: "var(--border-subtle)" }}
    />
  );
}
